import { useEffect, useCallback, useRef } from 'react';
import { useNotifications } from './useNotifications';

const WORKOUT_START_KEY = 'evolve-active-workout-start';
const REMINDER_1_SENT_KEY = 'evolve-workout-reminder-1-sent';
const REMINDER_2_SENT_KEY = 'evolve-workout-reminder-2-sent';

// Reminder times in milliseconds
const REMINDER_1_TIME = 90 * 60 * 1000; // 1.5 hours
const REMINDER_2_TIME = 120 * 60 * 1000; // 2 hours

interface WorkoutRemindersConfig {
  userName?: string;
}

export function useWorkoutReminders(config: WorkoutRemindersConfig = {}) {
  const { isEnabled, sendNotification } = useNotifications();
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start tracking a workout session
  const startWorkoutTracking = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(WORKOUT_START_KEY, now.toString());
    localStorage.removeItem(REMINDER_1_SENT_KEY);
    localStorage.removeItem(REMINDER_2_SENT_KEY);
    console.log('[WorkoutReminders] Started tracking workout at', new Date(now).toISOString());
  }, []);

  // Clear workout tracking (called when workout is completed)
  const completeWorkoutTracking = useCallback(() => {
    localStorage.removeItem(WORKOUT_START_KEY);
    localStorage.removeItem(REMINDER_1_SENT_KEY);
    localStorage.removeItem(REMINDER_2_SENT_KEY);
    console.log('[WorkoutReminders] Workout completed, cleared tracking');
  }, []);

  // Check if we need to send reminders
  const checkWorkoutReminders = useCallback(() => {
    const startTimeStr = localStorage.getItem(WORKOUT_START_KEY);
    if (!startTimeStr) return;

    const startTime = parseInt(startTimeStr, 10);
    const elapsed = Date.now() - startTime;
    const name = config.userName || 'Evolver';

    // Check 1.5 hour reminder
    if (elapsed >= REMINDER_1_TIME && !localStorage.getItem(REMINDER_1_SENT_KEY)) {
      if (isEnabled) {
        sendNotification(
          '💪 Still crushing it?',
          `Hey ${name}, you started a workout 1.5 hours ago. Don't forget to log your sets!`
        );
      }
      localStorage.setItem(REMINDER_1_SENT_KEY, 'true');
      console.log('[WorkoutReminders] Sent 1.5hr reminder');
    }

    // Check 2 hour reminder
    if (elapsed >= REMINDER_2_TIME && !localStorage.getItem(REMINDER_2_SENT_KEY)) {
      if (isEnabled) {
        sendNotification(
          '🔥 Finish strong!',
          `${name}, your workout session is over 2 hours now. Log those gains before you forget!`
        );
      }
      localStorage.setItem(REMINDER_2_SENT_KEY, 'true');
      console.log('[WorkoutReminders] Sent 2hr reminder');
    }
  }, [isEnabled, sendNotification, config.userName]);

  // Check if there's an active workout on mount
  const hasActiveWorkout = useCallback((): boolean => {
    return !!localStorage.getItem(WORKOUT_START_KEY);
  }, []);

  // Get elapsed time of active workout in minutes
  const getActiveWorkoutMinutes = useCallback((): number | null => {
    const startTimeStr = localStorage.getItem(WORKOUT_START_KEY);
    if (!startTimeStr) return null;
    const elapsed = Date.now() - parseInt(startTimeStr, 10);
    return Math.floor(elapsed / 60000);
  }, []);

  // Set up periodic check
  useEffect(() => {
    // Check immediately on mount
    checkWorkoutReminders();

    // Check every 5 minutes
    checkIntervalRef.current = setInterval(() => {
      checkWorkoutReminders();
    }, 5 * 60 * 1000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkWorkoutReminders]);

  return {
    startWorkoutTracking,
    completeWorkoutTracking,
    hasActiveWorkout,
    getActiveWorkoutMinutes,
  };
}
